// src/components/admin/dsat/reports/ArchivedReportViewer.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "../../../../firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { useReactToPrint } from "react-to-print";
import { BiArchiveOut } from "react-icons/bi";
import { RiErrorWarningFill } from "react-icons/ri";
import { FaCheckCircle } from "react-icons/fa";

// Sub-components
import DetailedQuestionReport from "./DetailedQuestionReport";
import PerformanceReport from "./PerformanceReport";
import SubcategoryBreakdown from "./SubcategoryBreakdown";
import SearchableStudentDropdown from "./SearchableStudentDropdown"; 

// ---------------- helpers ----------------
function toDateSafe(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateShort(v) {
  const d = toDateSafe(v);
  if (!d) return "—";
  return d.toLocaleDateString();
}

function buildAttemptLabel(attempt) {
  const title =
    attempt?.testTitle ||
    attempt?.testName ||
    attempt?.displayName ||
    (attempt?.testId ? `SAT Diagnostic (${attempt.testId})` : "SAT Diagnostic");

  const completed =
    attempt?.completedAt || attempt?.completed_on || attempt?.completedAtIso;
  const dateLabel = completed ? formatDateShort(completed) : "—";

  const score =
    attempt?.totalScore ??
    attempt?.totalScaled ??
    attempt?.totalScaledScore ??
    attempt?.score ??
    null;

  const scoreLabel = score == null ? "" : ` — ${score}`;

  return `${title} — Completed ${dateLabel}${scoreLabel}`;
}

const SAT_RW_TOTAL = 54;
const SAT_MATH_TOTAL = 44;

function resolveTotal(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function formatRawScoreDisplay(raw, total) {
  if (raw === "—" || raw == null || raw === "") return "—";
  return `${raw} / ${total}`;
}

async function fetchAttemptsForStudent(studentId) {
  const results = [];

  // 1) New Standard: Top-level diagnostic_results where studentId matches
  try {
    const q = query(
      collection(db, "diagnostic_results"),
      where("studentId", "==", studentId)
    );
    const snap = await getDocs(q);
    snap.forEach((d) => {
      const data = d.data();
      if (data.type === "ACT") return;
      // Only include completed ones
      if (d.data().status === 'completed' || d.data().completedAt) {
        results.push({ attemptId: d.id, ...d.data() });
      }
    });
  } catch (e) {
    console.warn("Error fetching diagnostic_results collection:", e);
  }

  // 2) Legacy Single Doc: diagnostic_results/{studentId}
  if (results.length === 0) {
    try {
      const legacyRef = doc(db, "diagnostic_results", studentId);
      const legacySnap = await getDoc(legacyRef);
      if (legacySnap.exists()) {
        const data = legacySnap.data() || {};
        if (data.status === "completed" || !!data.completedAt) {
          results.push({ attemptId: "legacy", ...data });
        }
      }
    } catch (e) { /* ignore */ }
  }

  return results;
}

// ---------------- styles ----------------
const styles = {
  page: {
    minHeight: "100vh",
    padding: "24px 16px",
    background:
      "radial-gradient(circle at top, #e0f2fe 0, #eef2ff 40%, #f9fafb 100%)",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },
  container: {
    maxWidth: 1100,
    margin: "0 auto",
  },

  topCard: {
    borderRadius: 18,
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(229,231,235,0.9)",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
    backdropFilter: "blur(8px)",
    // Overflow visible is needed for the dropdown to pop out
    overflow: "visible", 
  },

  header: {
    padding: "18px 18px 10px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  titleBlock: { display: "flex", flexDirection: "column", gap: 4 },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  },
  subtitle: {
    margin: 0,
    fontSize: 13,
    color: "#64748b",
    lineHeight: 1.35,
  },
  badge: {
    fontSize: 11,
    padding: "6px 10px",
    borderRadius: 999,
    background: "linear-gradient(90deg, #dbeafe, #e0e7ff)",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },

  controls: {
    padding: "12px 18px 16px",
    borderTop: "1px solid rgba(226,232,240,0.8)",
    display: "grid",
    gridTemplateColumns: "1.2fr 1.8fr",
    gap: 12,
    alignItems: "start",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 12,
    color: "#334155",
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  select: {
    width: "100%",
    fontSize: 14,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #d1d5db",
    backgroundColor: "#ffffff",
    outline: "none",
    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
    boxSizing: "border-box",
    height: 46,
    minHeight: 44,
  },
  hint: { fontSize: 12, color: "#64748b", marginTop: 2 },

  reportShell: {
    marginTop: 16,
    borderRadius: 18,
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
  },
  reportHeader: {
    padding: "16px 18px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    background: "linear-gradient(90deg, #ffffff, #f8fafc)",
  },
  studentName: { margin: 0, fontSize: 18, fontWeight: 900, color: "#0f172a" },
  studentEmail: { margin: "2px 0 0", fontSize: 13, color: "#64748b" },
  metaBlock: { textAlign: "right", color: "#64748b", fontSize: 12 },
  metaLabel: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: 2,
  },
  metaValue: { fontSize: 13, fontWeight: 700, color: "#0f172a" },

  tiles: {
    padding: "14px 18px 6px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 12,
  },
  tile: {
    borderRadius: 14,
    padding: "12px 12px",
    border: "1px solid #e5e7eb",
    background: "linear-gradient(145deg, #f8fafc, #eef2ff)",
  },
  tileValue: {
    fontSize: 26,
    fontWeight: 900,
    color: "#1d4ed8",
    marginBottom: 4,
  },
  tileLabel: { fontSize: 12, color: "#64748b" },

  actions: {
    padding: "12px 18px 16px",
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  btn: {
    fontSize: 13,
    padding: "9px 14px",
    borderRadius: 999,
    border: "1px solid transparent",
    cursor: "pointer",
    fontWeight: 700,
  },
  btnDark: {
    backgroundColor: "#0f172a",
    color: "#ffffff",
    boxShadow: "0 10px 20px rgba(15,23,42,0.16)",
  },
  btnBlue: {
    backgroundColor: "#1d4ed8",
    color: "#ffffff",
    boxShadow: "0 10px 20px rgba(37,99,235,0.25)",
  },
  btnGhost: {
    backgroundColor: "#ffffff",
    color: "#0f172a",
    borderColor: "#e5e7eb",
  },

  loading: {
    padding: 16,
    borderRadius: 14,
    background: "#f1f5f9",
    color: "#475569",
    textAlign: "center",
    border: "1px solid #e2e8f0",
  },
  error: {
    padding: 14,
    borderRadius: 14,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    fontSize: 13,
  },
  empty: { padding: "10px 18px 16px", color: "#64748b", fontSize: 13 },
};

const printPageStyle = `
@page { size: auto; margin: 16mm; }
body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #ffffff !important; }
.no-print { display: none !important; }
table, tr, td, th { break-inside: avoid; page-break-inside: avoid; }
`;

// ---------------- component ----------------
export default function ArchivedReportViewer() {
  const [searchParams] = useSearchParams();
  const urlStudentId = String(searchParams.get("studentId") || "").trim();
  const urlAttemptId = String(searchParams.get("attemptId") || "").trim();

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState("");

  const [selectedStudentId, setSelectedStudentId] = useState(urlStudentId);

  const [attempts, setAttempts] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [attemptsError, setAttemptsError] = useState("");
  const [selectedAttemptId, setSelectedAttemptId] = useState("");

  const [showDetails, setShowDetails] = useState(false);
  const [showIntegrityTooltip, setShowIntegrityTooltip] = useState(false);

  const reportRef = useRef(null);

  const handleUnarchiveStudent = async () => {
    if (!selectedStudentId || !window.confirm("Are you sure you want to move this student's reports to the Archive? They will be hidden from this view.")) return;

    try {
        const batch = writeBatch(db);
        
        // 1. Find all diagnostic results for this student
        const q = query(collection(db, "diagnostic_results"), where("studentId", "==", selectedStudentId));
        const snap = await getDocs(q);
        
        snap.forEach((doc) => {
        batch.update(doc.ref, { isArchived: false });
        });
        
        await batch.commit();

        // 3. Refresh the page or remove student from local state
        setStudents(prev => prev.filter(s => s.uid !== selectedStudentId));
        setSelectedStudentId("");
        alert("Student moved to Archive.");
    } catch (e) {
        console.error(e);
        alert("Error archiving student.");
    }
    };

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Student Diagnostic Report",
    pageStyle: printPageStyle,
  });

  // ------------------------------------------------
  // 1. Load Students from diagnostic_results Only
  // ------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    async function loadStudentsFromDiagnostics() {
      setStudentsLoading(true);
      setStudentsError("");

      try {
        const diagRef = collection(db, "diagnostic_results");
        const snapshot = await getDocs(diagRef);

        // Map: studentId -> mostRecentCompletedAtMillis
        const studentMap = new Map();

        snapshot.forEach((docSnap) => {
          const d = docSnap.data() || {};
          if (d.type === "ACT") return;
          if (!d.isArchived) return;
          const sid = d.studentId;
          if (!sid) return;

          // ✅ Only include completed attempts
          const isCompleted = d.status === "completed" || !!d.completedAt;
          if (!isCompleted) return;

          const dateObj = toDateSafe(d.completedAt || d.completed_on || d.completedAtIso);
          const timeVal = dateObj ? dateObj.getTime() : 0;

          // Keep most recent completion date per student
          if (!studentMap.has(sid)) {
            studentMap.set(sid, timeVal);
          } else if (timeVal > studentMap.get(sid)) {
            studentMap.set(sid, timeVal);
          }
        });

        const uniqueIds = Array.from(studentMap.keys());

        if (uniqueIds.length === 0) {
          if (!cancelled) setStudents([]);
          return;
        }

        const userPromises = uniqueIds.map(async (uid) => {
          try {
            const userSnap = await getDoc(doc(db, "users", uid));
            const userData = userSnap.exists() ? userSnap.data() : {};
            const lastMs = studentMap.get(uid) || 0;

            return {
              uid,
              name: userData.name || "Unknown Name",
              email: userData.email || "",
              lastCompletedAt: lastMs,
              lastDateLabel: lastMs > 0 ? new Date(lastMs).toLocaleDateString() : "N/A",
            };
          } catch (err) {
            console.error("Error fetching user", uid, err);
            const lastMs = studentMap.get(uid) || 0;
            return {
              uid,
              name: "Unknown (Error)",
              email: "",
              lastCompletedAt: lastMs,
              lastDateLabel: "N/A",
            };
          }
        });

        const loadedStudents = await Promise.all(userPromises);

        // ✅ Sort by most recent completion date (descending)
        loadedStudents.sort((a, b) => (b.lastCompletedAt || 0) - (a.lastCompletedAt || 0));

        if (!cancelled) setStudents(loadedStudents);
      } catch (e) {
        if (!cancelled) {
          setStudentsError(`Failed to load student list: ${e?.message || e}`);
        }
      } finally {
        if (!cancelled) setStudentsLoading(false);
      }
    }


    loadStudentsFromDiagnostics();
    return () => {
      cancelled = true;
    };
  }, []);

  // ------------------------------------------------
  // 2. Load attempts when student changes
  // ------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    async function loadAttempts() {
      setAttempts([]);
      setSelectedAttemptId("");
      setAttemptsError("");
      setShowDetails(false);

      if (!selectedStudentId) return;

      setAttemptsLoading(true);
      try {
        const list = await fetchAttemptsForStudent(selectedStudentId);
        if (cancelled) return;

        // Ensure newest -> oldest
        const sorted = [...list].sort((a, b) => {
          const da = toDateSafe(a?.completedAt) || new Date(0);
          const dbb = toDateSafe(b?.completedAt) || new Date(0);
          return dbb.getTime() - da.getTime();
        });

        setAttempts(sorted);
        const preferredAttemptId =
          urlAttemptId && sorted.some((a) => a.attemptId === urlAttemptId)
            ? urlAttemptId
            : sorted[0]?.attemptId || "";
        setSelectedAttemptId(preferredAttemptId);
      } catch (e) {
        if (!cancelled)
          setAttemptsError(`Failed to load attempts: ${e?.message || e}`);
      } finally {
        if (!cancelled) setAttemptsLoading(false);
      }
    }

    loadAttempts();
    return () => {
      cancelled = true;
    };
  }, [selectedStudentId, urlAttemptId]);

  const selectedStudent = useMemo(
    () => students.find((s) => s.uid === selectedStudentId) || null,
    [students, selectedStudentId]
  );

  const selectedAttempt = useMemo(
    () => attempts.find((a) => a.attemptId === selectedAttemptId) || null,
    [attempts, selectedAttemptId]
  );

  const isCompleted =
    selectedAttempt &&
    (selectedAttempt.status === "completed" || !!selectedAttempt.completedAt);

  const attemptTitle =
    selectedAttempt?.testTitle ||
    selectedAttempt?.testName ||
    selectedAttempt?.displayName ||
    "SAT Diagnostic";

  // Score fields (support multiple naming variants)
  const totalScore =
    selectedAttempt?.totalScore ??
    selectedAttempt?.totalScaled ??
    selectedAttempt?.totalScaledScore ??
    "—";
  const rwScaled =
    selectedAttempt?.rwScaledScore ?? selectedAttempt?.rwScaled ?? "—";
  const mathScaled =
    selectedAttempt?.mathScaledScore ?? selectedAttempt?.mathScaled ?? "—";
  const rwRaw =
    selectedAttempt?.rwRawScore ?? selectedAttempt?.rwRaw ?? "—";
  const mathRaw =
    selectedAttempt?.mathRawScore ?? selectedAttempt?.mathRaw ?? "—";
  const rwRawTotal = resolveTotal(
    selectedAttempt?.rwTotalQuestions ?? selectedAttempt?.englishTotalQuestions ?? selectedAttempt?.rwQuestionCount,
    SAT_RW_TOTAL
  );
  const mathRawTotal = resolveTotal(
    selectedAttempt?.mathTotalQuestions ?? selectedAttempt?.mathQuestionCount,
    SAT_MATH_TOTAL
  );
  const rwRawDisplay = formatRawScoreDisplay(rwRaw, rwRawTotal);
  const mathRawDisplay = formatRawScoreDisplay(mathRaw, mathRawTotal);

  const completedLabel = selectedAttempt?.completedAt
    ? formatDateShort(selectedAttempt.completedAt)
    : "—";

  if (studentsLoading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.loading}>Loading student reports…</div>
        </div>
      </div>
    );
  }

  if (studentsError) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.error}>{studentsError}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Top card: header + controls */}
        <div style={styles.topCard}>
          <div className="no-print" style={styles.header}>
            <div style={styles.titleBlock}>
              <h2 style={styles.title}>Archived - SAT Diagnostic Report</h2>
              <p style={styles.subtitle}>
                Select a student and a diagnostic attempt to view scores, skill
                breakdown, and question review.
              </p>
            </div>
            <div style={styles.badge}>Reports Dashboard</div>
          </div>

          <div className="no-print" style={styles.controls}>
            {/* UPDATED: Searchable Student Dropdown 
               Replaces the old <select>
            */}
            <div style={styles.field}>
              <div style={styles.label}>Student</div>
              <SearchableStudentDropdown
                students={students}
                selectedId={selectedStudentId}
                onSelect={(uid) => setSelectedStudentId(uid)}
                placeholder="Search for a student..."
              />
              <div style={styles.hint}>
                {selectedStudentId
                  ? "Student selected."
                  : "Pick a student to load attempts."}
              </div>
            </div>

            <div style={styles.field}>
              <div style={styles.label}>Attempt / Test</div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <select
                style={{
                  ...styles.select,
                  flex: 1,
                  opacity: selectedStudentId && !attemptsLoading ? 1 : 0.75,
                }}
                value={selectedAttemptId}
                onChange={(e) => {
                  setSelectedAttemptId(e.target.value);
                  setShowDetails(false);
                }}
                disabled={
                  !selectedStudentId ||
                  attemptsLoading ||
                  attempts.length === 0
                }
              >
                {!selectedStudentId && (
                  <option value="">Select a student first</option>
                )}
                {selectedStudentId && attemptsLoading && (
                  <option value="">Loading attempts…</option>
                )}
                {selectedStudentId &&
                  !attemptsLoading &&
                  attempts.length === 0 && (
                    <option value="">No completed attempts found</option>
                  )}
                {attempts.map((a) => (
                  <option key={a.attemptId} value={a.attemptId}>
                    {buildAttemptLabel(a)}
                  </option>
                ))}
              </select>
              
              {selectedStudentId && (
                <button
                  onClick={handleUnarchiveStudent}
                  title="Restore to Main List"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    height: 46,
                    padding: "0 16px",
                    borderRadius: 12,
                    border: "1px solid #bbf7d0",
                    background: "#f0fdf4",
                    color: "#166534",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <BiArchiveOut size={18} />
                  <span>Unarchive</span>
                </button>
              )}

              </div>

              <div style={styles.hint}>
                {attemptsError
                  ? attemptsError
                  : selectedStudentId
                  ? attemptsLoading
                    ? "Loading attempts…"
                    : attempts.length
                    ? `${attempts.length} attempt(s) found.`
                    : "No diagnostic attempts found for this student."
                  : " "}
              </div>
            </div>
          </div>
        </div>

        {/* Report body */}
        {selectedStudentId && !attemptsLoading && attempts.length === 0 && (
          <div style={styles.empty}>
            No diagnostic attempts found for this student.
          </div>
        )}

        {selectedStudent && selectedAttempt && (
          <div style={styles.reportShell} ref={reportRef}>
            <div style={styles.reportHeader}>
              <div>
                <h3 style={styles.studentName}>{selectedStudent.name}</h3>
                <p style={styles.studentEmail}>{selectedStudent.email}</p>
                <div
                  style={{
                    fontSize: 12,
                    color: "#64748b",
                    marginTop: 6,
                  }}
                >
                  <strong style={{ color: "#0f172a" }}>
                    {attemptTitle}
                  </strong>
                  {" • "}
                  Attempt ID: {selectedAttemptId}
                </div>
              </div>

              <div style={styles.metaBlock}>
                <div style={styles.metaLabel}>Completed on</div>
                <div style={styles.metaValue}>{completedLabel}</div>
                
                {/* Integrity Flag */}
                {selectedAttempt?.integrityLog && (
                  <div 
                    style={{ 
                      display: "flex", alignItems: "center", justifyContent: "flex-end", 
                      gap: 6, marginTop: 8, position: "relative", cursor: "help" 
                    }}
                    onMouseEnter={() => setShowIntegrityTooltip(true)}
                    onMouseLeave={() => setShowIntegrityTooltip(false)}
                  >
                    <span style={{ 
                      fontSize: 12, fontWeight: 700, 
                      color: selectedAttempt.integrityLog.cheated_triggered ? "#b91c1c" : "#15803d" 
                    }}>
                      {selectedAttempt.integrityLog.cheated_triggered ? "Integrity Flagged" : "Integrity Verified"}
                    </span>
                    
                    {selectedAttempt.integrityLog.cheated_triggered ? (
                      <RiErrorWarningFill size={18} color="#dc2626" />
                    ) : (
                      <FaCheckCircle size={16} color="#16a34a" />
                    )}

                    {/* Hover Tooltip */}
                    {showIntegrityTooltip && (
                      <div style={{
                        position: "absolute", top: "100%", right: 0, marginTop: 8, width: 200,
                        backgroundColor: "#1e293b", color: "#f8fafc", padding: "12px", 
                        borderRadius: 8, fontSize: 12, textAlign: "left", zIndex: 50, 
                        boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
                      }}>
                        <div style={{ fontWeight: 800, marginBottom: 8, borderBottom: "1px solid #334155", paddingBottom: 4 }}>
                          Integrity Log Details
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span>Tab Switches:</span> 
                          <strong>{selectedAttempt.integrityLog.tabSwitchCount}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span>Fullscreen Exits:</span> 
                          <strong>{selectedAttempt.integrityLog.fullscreenExitCount}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span>Inactive Time:</span> 
                          <strong>{selectedAttempt.integrityLog.totalInactiveTime} sec</strong>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {!isCompleted ? (
              <div style={styles.empty}>
                This attempt is not marked as completed.
              </div>
            ) : (
              <>
                {/* Score tiles */}
                <div style={styles.tiles}>
                  <div style={styles.tile}>
                    <div style={styles.tileValue}>{totalScore}</div>
                    <div style={styles.tileLabel}>Total Score</div>
                  </div>
                  <div style={styles.tile}>
                    <div style={styles.tileValue}>{rwScaled}</div>
                    <div style={styles.tileLabel}>
                      Reading &amp; Writing Scaled Score
                    </div>
                  </div>
                  <div style={styles.tile}>
                    <div style={styles.tileValue}>{mathScaled}</div>
                    <div style={styles.tileLabel}>
                      Math Scaled Score
                    </div>
                  </div>
                  <div style={styles.tile}>
                    <div style={styles.tileValue}>{rwRawDisplay}</div>
                    <div style={styles.tileLabel}>R/W Raw Score</div>
                  </div>
                  <div style={styles.tile}>
                    <div style={styles.tileValue}>{mathRawDisplay}</div>
                    <div style={styles.tileLabel}>Math Raw Score</div>
                  </div>
                </div>

                {/* Overall summary + difficulty + section analysis */}
                <div style={{ padding: "0 18px 4px" }}>
                  <PerformanceReport
                    studentId={selectedStudent.uid}
                    testId={selectedAttempt?.testId}
                    summary={selectedAttempt}
                  />
                </div>

                {/* Subcategory Breakdown (now following this attempt's answers) */}
                <div style={{ padding: "0 18px 6px" }}>
                  <SubcategoryBreakdown
                    studentId={selectedStudent.uid}
                    testId={selectedAttempt?.testId}
                    attemptId={selectedAttemptId}
                    attemptLabel={completedLabel}
                    answers={selectedAttempt?.answers}
                  />
                </div>

                <div className="no-print" style={styles.actions}>
                  <button
                    style={{ ...styles.btn, ...styles.btnDark }}
                    onClick={() => setShowDetails((v) => !v)}
                  >
                    {showDetails
                      ? "Hide Question Breakdown"
                      : "View Question Breakdown"}
                  </button>

                  <button
                    style={{ ...styles.btn, ...styles.btnBlue }}
                    onClick={() => handlePrint()}
                  >
                    🖨️ Print / Save as PDF
                  </button>
                </div>

                {showDetails && (
                  <>
                    <div style={{ padding: "0 18px 18px" }}>
                      <DetailedQuestionReport
                        studentId={selectedStudent.uid}
                        testId={selectedAttempt?.testId}
                        attemptId={selectedAttemptId}
                        showSubcategory={false}
                      />
                    </div>
                    <div className="no-print" style={{ padding: "0 18px 18px", display: "flex", justifyContent: "flex-end" }}>
                      <button
                        style={{ ...styles.btn, ...styles.btnGhost }}
                        onClick={() => {
                          setShowDetails(false);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Close &amp; Scroll Top
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {!selectedStudentId && (
          <div className="no-print" style={styles.empty}>
            Select a student above to view their report.
          </div>
        )}
      </div>
    </div>
  );
}
